import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, Mail, Linkedin, Quote, Award, Users } from "lucide-react";
import { TEAM, FIRM } from "@/data/content";
import { Breadcrumbs, FadeIn } from "@/components/Shared";

export default function PersonProfile() {
  const { slug } = useParams();
  const person = TEAM.find((p) => p.slug === slug);
  if (!person) return <Navigate to="/team" replace />;

  return (
    <main data-testid="person-profile-page">
      {/* Portrait hero on pale panel */}
      <section className="brand-panel-dark pb-16 pt-36 sm:pb-20 sm:pt-44">
        <div className="brand-container">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
            <FadeIn className="lg:col-span-7">
              <p className="brand-eyebrow-on-dark">
                {person.role}
              </p>
              <h1 className="mt-4 text-4xl font-light tracking-tight sm:text-5xl lg:text-6xl" data-testid="profile-name">
                {person.name}
              </h1>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                <a
                  href={`tel:${person.phone.replace(/\s/g, "")}`}
                  className="brand-link-on-dark flex items-center gap-2.5 text-sm"
                  data-testid="profile-phone"
                >
                  <Phone className="size-4 text-brand-terracotta" /> {person.phone}
                </a>
                <a
                  href={`mailto:${person.email}`}
                  className="brand-link-on-dark flex items-center gap-2.5 text-sm"
                  data-testid="profile-email"
                >
                  <Mail className="size-4 text-brand-terracotta" /> {person.email}
                </a>
                {person.linkedin && (
                  <a
                    href={person.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="brand-link-on-dark flex items-center gap-2.5 text-sm"
                    data-testid="profile-linkedin"
                  >
                    <Linkedin className="size-4 text-brand-terracotta" /> LinkedIn
                  </a>
                )}
              </div>
            </FadeIn>
            <FadeIn delay={0.15} className="lg:col-span-5">
              <img
                src={person.image}
                alt={person.name}
                className="w-full max-w-sm border border-white/10 object-cover lg:ml-auto"
              />
            </FadeIn>
          </div>
        </div>
      </section>

      <Breadcrumbs items={[{ label: "Team", href: "/team" }, { label: person.name }]} />

      {/* Quotes */}
      {person.quotes && (
        <section className="brand-panel-pale py-20 sm:py-28 lg:py-32">
          <div className="brand-container grid grid-cols-1 gap-8 md:grid-cols-3">
            {person.quotes.map((q, i) => (
              <FadeIn key={i} delay={i * 0.1}>
                <figure className="border-l-2 border-brand-gold pl-5">
                  <Quote className="size-5 text-brand-gold" />
                  <blockquote className="mt-3 text-base font-light leading-relaxed text-slate-700">
                    "{q.text}"
                  </blockquote>
                  <figcaption className="mt-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                    {q.source}
                  </figcaption>
                </figure>
              </FadeIn>
            ))}
          </div>
        </section>
      )}

      {/* Bio + sidebar */}
      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            <div className="lg:col-span-8">
              <FadeIn>
                <h2 className="brand-heading text-2xl sm:text-3xl">
                  Profile
                </h2>
                <div className="mt-5 space-y-4">
                  {person.bio.map((p, i) => (
                    <p key={i} className="leading-relaxed text-slate-600">{p}</p>
                  ))}
                </div>
              </FadeIn>

              {person.cases && (
                <FadeIn delay={0.1}>
                  <h2 className="brand-heading mt-14 text-2xl sm:text-3xl">
                    High profile cases
                  </h2>
                  <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2">
                    {person.cases.map((c, i) => (
                      <div key={i} className="border border-slate-200 bg-brand-ivory p-6">
                        <p className="font-medium text-brand-navy">{c.title}</p>
                        <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.text}</p>
                      </div>
                    ))}
                  </div>
                </FadeIn>
              )}
            </div>

            <aside className="lg:col-span-4">
              <FadeIn delay={0.1}>
                {person.awards && (
                  <div className="border border-slate-200 bg-white p-6">
                    <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                      <Award className="size-4 text-brand-gold" /> Awards & achievements
                    </p>
                    <ul className="mt-4 space-y-3">
                      {person.awards.map((a, i) => (
                        <li key={i} className="border-l-2 border-brand-gold/40 pl-3 text-sm leading-snug text-slate-600">
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {person.memberships && (
                  <div className="brand-panel-pale mt-6 border border-slate-200 p-6">
                    <p className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-400">
                      <Users className="size-4 text-brand-gold" /> Memberships
                    </p>
                    <ul className="mt-4 space-y-2.5">
                      {person.memberships.map((m, i) => (
                        <li key={i} className="text-sm text-slate-600">{m}</li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Contact card */}
                <div className="brand-panel-dark mt-6 p-6" data-testid="profile-contact-card">
                  <p className="text-lg font-light">Speak to {person.name.split(" ")[0]}.</p>
                  <div className="mt-4 space-y-2.5 text-sm">
                    <a href={`tel:${person.phone.replace(/\s/g, "")}`} className="brand-link-on-dark flex items-center gap-2.5" data-testid="profile-card-office-phone">
                      <Phone className="size-4 text-brand-terracotta" /> Office: {person.phone}
                    </a>
                    <a href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`} className="brand-link-on-dark flex items-center gap-2.5" data-testid="profile-card-emergency-phone">
                      <Phone className="size-4 text-brand-terracotta" /> 24hr: {FIRM.emergencyPhone}
                    </a>
                    <a href={`mailto:${person.email}`} className="brand-link-on-dark flex items-center gap-2.5" data-testid="profile-card-email">
                      <Mail className="size-4 text-brand-terracotta" /> {person.email}
                    </a>
                  </div>
                  <Link
                    to="/contact"
                    className="brand-cta-primary mt-5 block py-3 text-center"
                    data-testid="profile-contact-cta"
                  >
                    Make an enquiry
                  </Link>
                </div>
              </FadeIn>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
